// Phase 107 — Universal Profile Photo
//
// The single rendering primitive for ANY identity image across the app.
// • If `entity.profile_photo_url` (or legacy alias `profile_photo_path` / `photo_url`) is set,
//   we render the real photo via a normalized URL.
// • Otherwise we render a clean dual-initials circle (bg-slate-100 / text-slate-700).
// • NO 3D sport-themed SVG, NO pravatar / generated avatars — those were
//   deprecated in Phase 107 to eliminate broken-asset states and visual clutter.
//
// Used by: AppShell header roundel, dashboard greeting card, students table,
//          HRMS table, parent portal child switcher, drill author byline, chat
//          bubbles, document letterhead seal, etc.

import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { bestPhotoFor } from '@/lib/photo';
import { User } from 'lucide-react';

function initialsOf(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => (p[0] || '')).join('').toUpperCase() || '?';
}

/**
 * Universal Profile Picture.
 *
 * @param {object} props
 * @param {string} props.name        — used both for `alt` and the initials fallback
 * @param {object} [props.entity]    — student / employee / user object (carries profile_photo_url)
 * @param {string} [props.photoUrl]  — explicit URL override (wins over entity.profile_photo_url)
 * @param {string} [props.size]      — Tailwind sizing classes, e.g. 'h-8 w-8'. Default 'h-8 w-8'.
 * @param {string} [props.className] — additional classes
 * @param {string} [props.testId]    — optional data-testid pass-through
 * @param {'initials'|'silhouette'} [props.fallback]
 *        — 'initials' (default) shows the dual-letter chip — best for lists where identification matters.
 *        — 'silhouette' shows a clean User icon — preferred for global header roundels per Phase 122.
 */
export default function UniversalProfilePic({
  name,
  entity = null,
  photoUrl = null,
  size = 'h-8 w-8',
  className = '',
  testId,
  fallback = 'initials',
}) {
  const resolved = photoUrl || (entity ? bestPhotoFor(entity) : null);
  const safeName = name || entity?.name || entity?.full_name || '';
  const initials = initialsOf(safeName);

  return (
    <Avatar
      className={`${size} ${className}`.trim()}
      data-testid={testId}
      title={safeName || undefined}
    >
      {resolved && <AvatarImage src={resolved} alt={safeName || 'Profile'} className="object-cover" />}
      <AvatarFallback className="bg-slate-100 text-slate-700 text-xs font-medium">
        {fallback === 'silhouette' ? (
          <User className="h-1/2 w-1/2 text-slate-400" strokeWidth={1.8} />
        ) : (
          initials
        )}
      </AvatarFallback>
    </Avatar>
  );
}
